import { useMemo } from "react";
import { Calendar } from "primereact/calendar";
import PropTypes from "prop-types";
import { getErrorMessage } from "../form/FormStandardProps";
import Label from "./Label";

const DatePicker = ({
  className = "",
  disabled = false,
  input,
  label,
  meta: { touched, error },
  placeholder,
  readOnly = false,
  required = false,
  minDate: requestedMinDate,
  ...rest
}) => {
  // The field picks whole days, so its minimum is the start of a day. Computed once (and kept
  // while the screen's own minDate is the same day): a minimum recomputed every render as the
  // current instant made the Calendar refuse, then drop, a value typed as today's midnight.
  // A screen that passes no minDate gets today; one that passes null gets no minimum.
  const minDayTime =
    requestedMinDate === undefined
      ? new Date().setHours(0, 0, 0, 0)
      : requestedMinDate && new Date(requestedMinDate).setHours(0, 0, 0, 0);
  const minDate = useMemo(
    () => (minDayTime ? new Date(minDayTime) : null),
    [minDayTime]
  );

  return (
    <div
      data-testid={`field-${input.name}`}
      className={`edexlms-field text-input ${readOnly ? "readonly" : ""} ${
        disabled ? "disabled" : ""
      } `}
    >
      {(label && (
        <Label
          label={label}
          disabled={disabled}
          required={required}
          htmlFor={input.name}
        />
      )) ||
        null}
      <Calendar 
        {...input}
        {...rest}
        minDate={minDate}
        disabled={disabled || readOnly}
        placeholder={placeholder}
         onChange={(e) => {
    input.onChange(e.value); // keep Final Form in sync ✅
    if (rest.onChange) rest.onChange(e.value); // call parent handler if provided ✅
  }}
        showIcon
        dateFormat="dd MM yy"
        invalid={touched && error}
        className={`edexlms-field-input w-full ${className}`}
      />
      {(touched && error && (
        <div className="edexlms-field-error">
          {getErrorMessage(
            error,
            input,
            { ...rest, minDate },
            label || placeholder
          )}
        </div>
      )) ||
        null}
    </div>
  );
};

DatePicker.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  input: PropTypes.object,
  label: PropTypes.string,
  meta: PropTypes.object,
  placeholder: PropTypes.string,
  minDate: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string,
  ]),
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
};

export default DatePicker;
